import { EntityTypes } from "../../EntityTypes";
import { IFactoryResource } from "../../factories";
import { ILanguageResource } from "../../languages";
import { ILocationResource } from "../../locations";
import { ITrainingTemplateResource } from "../../training-templates";
import { createApiMutationHook, IApiMutationFunction } from "../../utils/api";
import { mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";
import { getResourceUuid } from "../../utils/getResourceUuid";
import { IClassroom } from "../types/IClassroom";

export type IClassroomCreateWithTemplateActionParameters = {
    erpIdentifier: string;
    erpFile?: File;
};

export type IClassroomCreateWithTemplateMutationParameters = Pick<IClassroom, "title" | "description"> & {
    trainingTemplate: RelationalValue<ITrainingTemplateResource>;
    dailyStartHour?: string;
    dailyEndHour?: string;
    location: RelationalValue<ILocationResource>;
    language: RelationalValue<ILanguageResource>;
    factory: RelationalValue<IFactoryResource>;
    isBadgesEnabled?: boolean;
    isReflexiveTutorEnabled?: boolean;
    trainingActions: IClassroomCreateWithTemplateActionParameters[];
};

export type IClassroomCreateWithTemplateMutationResult = IClassroom;

type TPayloadValue = string | boolean | undefined;

export const classroomCreateWithTemplateMutation: IApiMutationFunction<
    IClassroomCreateWithTemplateMutationParameters
> = ({ trainingTemplate, location, language, factory, trainingActions, ...restData }) => {
    const payload: Record<string, TPayloadValue> = {
        ...restData,
        location: mapRelationalValueAsIRI(EntityTypes.LOCATION, location),
        language: mapRelationalValueAsIRI(EntityTypes.LANGUAGE, language),
        factory: mapRelationalValueAsIRI(EntityTypes.FACTORY, factory),
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
        if (undefined === value) {
            return;
        }

        formData.append(key, value.toString());
    });

    trainingActions.forEach((action, index) => {
        formData.append(`trainingActions[${index}][erpIdentifier]`, action.erpIdentifier);

        if (action.erpFile) {
            formData.append(`trainingActions[${index}][erpFile]`, action.erpFile);
        }
    });

    return {
        method: "POST",
        url: `/training-templates/${getResourceUuid(trainingTemplate)}/classroom`,
        headers: {
            "Content-Type": "multipart/form-data",
        },
        data: formData,
    };
};

export const useClassroomCreateWithTemplateMutation = createApiMutationHook<
    IClassroomCreateWithTemplateMutationParameters,
    IClassroomCreateWithTemplateMutationResult
>(classroomCreateWithTemplateMutation);
